import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

export default function PaymentSuccess() {
  const { tr } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= 8) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const CONTENT = {
    checking: {
      icon: <Clock size={40} className="text-[#007A8A] animate-pulse" />,
      title: { it: "Verifica del pagamento…", en: "Verifying payment…" },
      body: { it: "Attendi qualche secondo, stiamo confermando la cauzione con Stripe.", en: "Please wait a few seconds while we confirm your deposit with Stripe." },
    },
    paid: {
      icon: <CheckCircle2 size={40} className="text-[#007A8A]" />,
      title: { it: "Cauzione confermata.", en: "Deposit confirmed." },
      body: {
        it: "Grazie! La cauzione di € 150 è stata registrata. Ti contatteremo entro 24 ore per confermare la data della prova con il nostro tecnico.",
        en: "Thank you! Your € 150 deposit has been received. We will contact you within 24 hours to confirm the trial date with our technician.",
      },
    },
    pending: {
      icon: <Clock size={40} className="text-[#FF4D00]" />,
      title: { it: "Pagamento in elaborazione.", en: "Payment processing." },
      body: {
        it: "Il pagamento risulta ancora in elaborazione. Riceverai conferma via email; per dubbi scrivici a info@aerastechnology.it.",
        en: "Your payment is still being processed. You will receive a confirmation by email; for questions write to info@aerastechnology.it.",
      },
    },
    error: {
      icon: <AlertTriangle size={40} className="text-[#FF4D00]" />,
      title: { it: "Qualcosa non ha funzionato.", en: "Something went wrong." },
      body: {
        it: "Non siamo riusciti a verificare il pagamento. Riprova o contattaci a info@aerastechnology.it.",
        en: "We could not verify the payment. Please try again or contact us at info@aerastechnology.it.",
      },
    },
  };

  const c = CONTENT[state];

  return (
    <div data-testid="page-payment-success" className="bg-white text-[#0a0a0a] min-h-screen flex items-center pt-32 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 w-full">
        <div className="max-w-xl border border-[#e4e4e7] bg-[#f4f4f5] p-12 md:p-16">
          {c.icon}
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter mt-8 mb-6" data-testid="payment-status-title">
            {tr(c.title)}
          </h1>
          <p className="font-body text-[#3f3f46] leading-relaxed mb-10">{tr(c.body)}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/" className="btn-primary" data-testid="payment-home-link">
              {tr({ it: "Torna alla home", en: "Back to home" })} →
            </Link>
            {state === "error" && (
              <Link to="/prova-a-casa" className="btn-ghost">
                {tr({ it: "Riprova", en: "Try again" })}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
